import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { format, isToday, parseJSON } from "date-fns";
import { es } from "date-fns/locale";
import NumberFormat from "react-number-format";
import { makeStyles } from "@material-ui/core/styles";
import Backdrop from "@material-ui/core/Backdrop";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionActions from "@material-ui/core/AccordionActions";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import CardActionArea from "@material-ui/core/CardActionArea";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Link from "@material-ui/core/Link";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import HistoryIcon from "@material-ui/icons/History";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import PersonIcon from "@material-ui/icons/Person";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import Alert from "@material-ui/lab/Alert";
import RoomIcon from "@material-ui/icons/Room";
import ForumIcon from "@material-ui/icons/Forum";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import PrintIcon from "@material-ui/icons/Print";
import ImageIcon from "@material-ui/icons/Image";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EmailIcon from "@material-ui/icons/Email";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import logger from "../logger";
import { formatCelular } from "../functions";
import { AppContext } from "../views/App";
import { ESTADOS_TICKET, DELIVERY_OPTIONS, PAYMENT_OPTIONS } from "./Ventas/constants";
import { getEstadoInfo } from "./Ventas/functions";
import ImageLoader from "./ImageLoader";
import { updateEstadoTicket } from "./Ticket";

const useStyles = makeStyles(theme => ({
  backButton: {
    marginRight: theme.spacing(2)
  },
  historyButton: {
    marginLeft: theme.spacing(2)
  },
  printButton: {
    marginLeft: theme.spacing(2),
    color: "white",
    "&:hover": {
      color: "white"
    }
  },
  title: {
    flexGrow: 1
  },
  container: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    overflow: "auto"
  },
  summary: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  estado: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  cardHeader: {
    paddingBottom: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  cardActions: {
    justifyContent: "flex-end"
  },
  comprobante: {
    backgroundColor: theme.palette.grey[100]
  }
}));

const subdomainPrefix = process.env.REACT_APP_ENV === "dev" ? "dev" : "";

const defaultEstado = { value: -1, label: "", icon: () => <></> };

const estados = [
  ESTADOS_TICKET.recibido,
  ESTADOS_TICKET.enEmpaque,
  ESTADOS_TICKET.listoParaEntregar,
  ESTADOS_TICKET.enEntrega,
  ESTADOS_TICKET.entregado,
  ESTADOS_TICKET.pedidoConIncidente,
  ESTADOS_TICKET.cancelado
];

const EstadosDialog = ({ open, value: valueProp, onClose }) => {
  const [value, setValue] = useState(valueProp);
  const radioGroupRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    }
  }, [valueProp, open]);

  const closing = value => {
    const estado = getEstadoInfo(value);
    onClose(estado);
  };

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleChange = ({ target }) => {
    const val = Number(target.value);
    setValue(val);
    closing(val);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={() => onClose(null)}
      onEntering={handleEntering}
    >
      <DialogTitle>Cambiar estado</DialogTitle>

      <DialogContent>
        <RadioGroup ref={radioGroupRef} value={value} onChange={handleChange}>
          {estados.map((e, i) => (
            <FormControlLabel
              key={`estado-${i}`}
              value={e.value}
              label={e.label}
              control={<Radio color="primary" />}
            />
          ))}
        </RadioGroup>
      </DialogContent>
    </Dialog>
  );
};

const PedidoDetails = ({ mobile, pedido, onClose, onUpdate }) => {
  const classes = useStyles();
  const { usuario } = useContext(AppContext);
  const [expanded, setExpanded] = useState(false);
  const [estadoInicial, setEstadoInicial] = useState(defaultEstado);
  const [estado, setEstado] = useState(defaultEstado);
  const [sending, setSending] = useState(false);
  const [openEstadoDialog, setOpenEstadoDialog] = useState(false);
  const notaRef = useRef(null);

  const { date, today, itemsCount, delivery, payment } = useMemo(() => {
    let date = null;
    let today = null;
    let itemsCount = null;
    let delivery = null;
    let payment = null;

    if (pedido) {
      date = parseJSON(pedido.fecha_creacion_ticket);
      today = isToday(date);

      itemsCount = `${pedido.body_ticket.carrito.items.length} Producto${
        pedido.body_ticket.carrito.items.length === 1 ? "" : "s"
      }`;

      delivery = {};

      if (pedido.body_ticket.despacho) {
        switch (pedido.body_ticket.despacho.tipo) {
          case DELIVERY_OPTIONS.delivery:
            const addressJson = pedido.body_ticket.despacho.deliveryAddress;
            let addressText = "";

            if (addressJson.description) {
              addressText = addressJson.description;
            } else {
              addressText = `${addressJson.addressName} ${addressJson.addressNumber}, ${addressJson.addressComuna}`;
            }

            delivery.icon = <LocalShippingIcon />;
            delivery.title = "Entrega a domicilio";
            delivery.subtitle = addressText;
            delivery.content = () => (
              <Typography color="textSecondary" variant="body2">
                {(pedido.body_ticket.despacho.deliveryService || {}).name}
                <br />
                {addressText}
                <br />
                {pedido.body_ticket.despacho.deliveryAddress2}
                {pedido.body_ticket.despacho.deliveryNote && (
                  <>
                    <br />
                    <br />
                    <em>{pedido.body_ticket.despacho.deliveryNote}</em>
                  </>
                )}
              </Typography>
            );
            break;
          case DELIVERY_OPTIONS.pickup:
            delivery.icon = <RoomIcon />;
            delivery.title = "Retiro en tienda";
            delivery.subtitle = "";
            delivery.content = () => (
              <>
                {(pedido.body_ticket.despacho.pickupOption === "other" ||
                  pedido.body_ticket.despacho.pickupNote) && (
                  <Typography color="textSecondary" variant="body2">
                    {pedido.body_ticket.despacho.pickupOption === "other" && (
                      <>
                        Retira {pedido.body_ticket.despacho.pickupPerson}
                        <br />
                      </>
                    )}
                    {pedido.body_ticket.despacho.pickupNote && (
                      <>
                        <br />
                        <em>{pedido.body_ticket.despacho.pickupNote}</em>
                      </>
                    )}
                  </Typography>
                )}
              </>
            );
            break;
          case DELIVERY_OPTIONS.convenience:
            delivery.icon = <ForumIcon />;
            delivery.title = "A convenir";
            delivery.subtitle = "";
            delivery.content = () => (
              <>
                {pedido.body_ticket.despacho.description && (
                  <Typography color="textSecondary" variant="body2">
                    {pedido.body_ticket.despacho.description}
                  </Typography>
                )}
              </>
            );
            break;
          case DELIVERY_OPTIONS.none:
            delivery.icon = <ReportProblemIcon />;
            delivery.title = "Sin delivery";
            delivery.subtitle = "";
            delivery.content = () => (
              <Typography color="textSecondary" variant="body2">
                Debe coordinar la entrega con el comprador
              </Typography>
            );
            break;
          default:
            break;
        }
      }

      if (pedido.body_ticket.carrito.payment) {
        payment = {};

        switch (pedido.body_ticket.carrito.payment.type) {
          case PAYMENT_OPTIONS.webpay.str:
          case PAYMENT_OPTIONS.onepay.str:
            const estado = pedido.body_ticket.carrito.payment.details.transaction
              ? pedido.body_ticket.carrito.payment.details.transaction.status === "success"
                ? { color: "textSecondary", label: "Aprobado" }
                : pedido.body_ticket.carrito.status === "cancelado"
                ? { color: "error", label: "Rechazado" }
                : { color: "textSecondary", label: "Pendiente" }
              : { color: "textSecondary", label: "Pendiente" };

            payment.title =
              pedido.body_ticket.carrito.payment.type === PAYMENT_OPTIONS.webpay.str
                ? "WebPay Plus"
                : "OnePay";
            payment.icon = <CreditCardIcon />;
            payment.subtitle = (
              <Typography color={estado.color} variant="body2">
                {estado.label}
              </Typography>
            );
            payment.content = () => (
              <Grid container>
                <Grid item xs={6}>
                  <Typography color="textSecondary" variant="body2">
                    Estado
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color={estado.color} variant="body2" align="right">
                    {estado.label}
                  </Typography>
                </Grid>

                {pedido.body_ticket.carrito.payment.details.transaction && (
                  <>
                    <Grid item xs={6}>
                      <Typography color="textSecondary" variant="body2">
                        Comprobante
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography color="textSecondary" variant="body2" align="right">
                        {pedido.body_ticket.carrito.payment.details.transaction.order}
                      </Typography>
                    </Grid>

                    {pedido.body_ticket.carrito.payment.details.transaction.payment && (
                      <>
                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2">
                            Transacción
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2" align="right">
                            {
                              pedido.body_ticket.carrito.payment.details.transaction.payment
                                .transaction_id
                            }
                          </Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2">
                            Autorización
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary" variant="body2" align="right">
                            {
                              pedido.body_ticket.carrito.payment.details.transaction.payment
                                .authorization_code
                            }
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            );
            break;
          case PAYMENT_OPTIONS.transferencia.str:
            payment.title = "Transferencia";
            payment.icon = <AccountBalanceIcon />;
            payment.subtitle = payment.subtitle = (
              <Typography color="textSecondary" variant="body2">
                Comprobante de pago
              </Typography>
            );
            payment.content = () => (
              <>
                {pedido.body_ticket.carrito.payment.details.comprobante && (
                  <List>
                    <ListItem
                      button
                      className={classes.comprobante}
                      onClick={() =>
                        openFile(pedido.body_ticket.carrito.payment.details.comprobante.key)
                      }
                    >
                      <ListItemIcon>
                        {pedido.body_ticket.carrito.payment.details.comprobante.type.includes(
                          "pdf"
                        ) ? (
                          <PictureAsPdfIcon />
                        ) : (
                          <ImageIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={pedido.body_ticket.carrito.payment.details.comprobante.name}
                      />
                      <ListItemSecondaryAction
                        onClick={() =>
                          openFile(pedido.body_ticket.carrito.payment.details.comprobante.key)
                        }
                      >
                        <VisibilityIcon />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                )}

                <Alert severity="info" className="mt-2">
                  <strong>Recuerda</strong> revisar tu cuenta bancaria para validar el pago.
                </Alert>
              </>
            );
            break;
          default:
            payment.title = "";
            payment.icon = null;
            payment.subtitle = "";
            payment.content = () => <></>;
            break;
        }
      }
    }

    return { date, today, itemsCount, delivery, payment };
  }, [pedido, classes]);

  useEffect(() => {
    if (pedido) {
      const estado = getEstadoInfo(pedido.estado_ticket);
      setEstadoInicial(estado);
      setEstado(estado);
    }
  }, [pedido]);

  const openEstadosDialog = () => {
    setOpenEstadoDialog(true);
  };

  const closeEstadosDialog = value => {
    if (value) {
      setEstado(value);
    }
    setOpenEstadoDialog(false);
  };

  const expandPanel = panel => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const updateEstado = async () => {
    if (estado.value !== pedido.estado_ticket) {
      setSending(true);

      try {
        await updateEstadoTicket({
          id_ticket: pedido.id_ticket,
          estado: estado.value,
          actividad: JSON.stringify({
            tipo: "ticket",
            contenido: `${usuario.primer_nombre_usuario} ${usuario.apellido_paterno_usuario} ha cambiado el estado a ${estado.label}`,
            estado_previo: pedido.estado_ticket
          }),
          id_usuario: usuario.id_usuario
        });

        setEstadoInicial(estado);
        onUpdate({ ...pedido, estado_ticket: estado.value });
      } catch (err) {
        logger("PedidoDetails.updateEstado.error", err);
      } finally {
        setSending(false);
      }
    }
  };

  const cancelUpdateEstado = () => {
    setEstado(getEstadoInfo(pedido.estado_ticket));
    setExpanded(false);
  };

  const updateNota = async () => {
    setSending(true);

    try {
      const nota = notaRef.current.value;
    } catch (err) {
      logger("PedidoDetails.updateNota.error", err);
    } finally {
      setSending(false);
    }
  };

  const cancelUpdateNota = () => {
    //notaRef.current.value = "";
    setExpanded(false);
  };

  const openFile = async key => {
    window.open(`https://${subdomainPrefix}media.carritofacil.cl/?key=${key}`, "_blank");
  };

  const Fecha = () => {
    return (
      <Typography className="p-2" component="p" variant="caption" align="right">
        {`Creado ${today ? "hoy," : "el"} ${format(date, today ? "p" : "dd MMM, p", {
          locale: es
        })}`}
      </Typography>
    );
  };

  const Estado = () => {
    return (
      <>
        <EstadosDialog open={openEstadoDialog} value={estado.value} onClose={closeEstadosDialog} />

        <Card>
          <CardActionArea onClick={openEstadosDialog}>
            <CardContent className={classes.estado}>
              <div>
                <estado.icon color="primary" />
                <Typography className="ml-3" component="span" variant="h5">
                  {estado.label}
                </Typography>
              </div>
              <ExpandMoreIcon />
            </CardContent>
          </CardActionArea>

          {estado.value !== estadoInicial.value && (
            <CardActions className={classes.cardActions}>
              <Button size="small" onClick={cancelUpdateEstado}>
                Cancelar
              </Button>
              <Button size="small" color="primary" onClick={updateEstado}>
                Aceptar
              </Button>
            </CardActions>
          )}
        </Card>
      </>
    );
  };

  const Nota = () => {
    return (
      <Accordion expanded={expanded === "nota"} onChange={expandPanel("nota")}>
        <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
          <ListItemIcon>
            <NoteAddIcon />
          </ListItemIcon>
          <ListItemText primary={expanded === "nota" ? "Nota" : "Agregar nota"} />
        </AccordionSummary>

        <AccordionDetails>
          <Container maxWidth="xs">
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Agregar nota"
              color="primary"
              variant="standard"
              inputProps={{
                ref: notaRef
              }}
            />
          </Container>
        </AccordionDetails>

        <AccordionActions>
          <Button size="small" onClick={cancelUpdateNota}>
            Cancelar
          </Button>
          <Button size="small" color="primary" onClick={updateNota}>
            Aceptar
          </Button>
        </AccordionActions>
      </Accordion>
    );
  };

  const Productos = () => {
    return (
      <List disablePadding dense>
        {pedido.body_ticket.carrito.items.map((item, index) => (
          <ListItem key={`item-${index}`}>
            {item.product.medias && item.product.medias.length > 0 && (
              <ListItemAvatar>
                <ImageLoader
                  imgKey={item.product.medias[0].key}
                  alt={item.product.name}
                  avatar
                  thumb
                />
              </ListItemAvatar>
            )}
            <ListItemText
              primary={item.product.name}
              secondary={
                <>
                  <span className="d-block">
                    {(item.body.features || []).length > 0 && (
                      <>
                        {item.body.features.map((f, i) => (
                          <span key={`f-${item.product.id}-${i}`}>
                            {f.label} {f.option.label}
                            {i < item.body.features.length - 1 && ", "}
                          </span>
                        ))}
                        <br />
                      </>
                    )}
                  </span>
                  <span className="d-block">
                    <NumberFormat
                      value={item.total}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="$"
                      displayType={"text"}
                    />
                    {item.quantity > 1 && (
                      <>
                        {" ("}
                        <NumberFormat
                          value={item.price}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="$"
                          displayType={"text"}
                        />
                        {" x "}
                        {item.quantity}
                        {")"}
                      </>
                    )}
                  </span>
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  };

  const Totales = () => {
    return (
      <Grid container>
        {pedido.body_ticket.pago.subtotal !== pedido.body_ticket.pago.total && (
          <>
            <Grid item xs={6}>
              <Typography color="textSecondary" variant="body2">
                Subtotal
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="textSecondary" variant="body2" align="right">
                <NumberFormat
                  value={pedido.body_ticket.pago.subtotal || 0}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="$"
                  displayType={"text"}
                />
              </Typography>
            </Grid>
          </>
        )}
        {pedido.body_ticket.despacho &&
          pedido.body_ticket.despacho.tipo === DELIVERY_OPTIONS.delivery &&
          pedido.body_ticket.carrito.payment.details.deliveryFee > 0 && (
            <>
              <Grid item xs={6}>
                <Typography color="textSecondary" variant="body2">
                  Despacho
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography color="textSecondary" variant="body2" align="right">
                  <NumberFormat
                    value={pedido.body_ticket.carrito.payment.details.deliveryFee}
                    thousandSeparator="."
                    decimalSeparator=","
                    prefix="$"
                    displayType={"text"}
                  />
                </Typography>
              </Grid>
            </>
          )}
        <Grid item xs={6}>
          <Typography color="textSecondary" variant="body2">
            Total
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography color="textSecondary" variant="body2" align="right">
            <NumberFormat
              value={pedido.body_ticket.pago.total || 0}
              thousandSeparator="."
              decimalSeparator=","
              prefix="$"
              displayType={"text"}
            />
          </Typography>
        </Grid>
      </Grid>
    );
  };

  if (!pedido) {
    return null;
  }

  return (
    <>
      <Backdrop className="backdrop" open={sending}>
        <CircularProgress />
      </Backdrop>

      <div>
        <AppBar position="sticky">
          <Toolbar>
            {onClose && (
              <IconButton
                className={classes.backButton}
                edge="start"
                color="inherit"
                onClick={onClose}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Typography className={classes.title} variant="h6">
              {`#${pedido.folio_ticket} ${
                pedido.contacto ? pedido.contacto.nombre_contacto : pedido.nombre_ticket
              }`}
            </Typography>

            {/* TODO}
            <IconButton className={classes.historyButton} edge="end" color="inherit">
              <HistoryIcon />
            </IconButton>
            {*/}

            <Link
              href={`/pedido/${pedido.id_ticket}`}
              className={classes.printButton}
              target="_blank"
              color="textPrimary"
              children={
                <IconButton edge="end" color="inherit">
                  <PrintIcon />
                </IconButton>
              }
            />
          </Toolbar>
        </AppBar>

        {mobile ? (
          <Container className={classes.container} disableGutters maxWidth="sm">
            <Paper square>
              <Fecha />

              <Estado />

              {/*<Nota />*/}

              <Accordion expanded={expanded === "cliente"} onChange={expandPanel("cliente")}>
                <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  {expanded === "cliente" ? (
                    <ListItemText primary="Cliente" />
                  ) : (
                    <ListItemText
                      primary={pedido.contacto.nombre_contacto}
                      secondary={
                        <>
                          <Typography variant="caption" color="textSecondary">
                            {formatCelular(pedido.contacto.body_contacto.telefono)}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="textSecondary">
                            {pedido.contacto.body_contacto.email}
                          </Typography>
                        </>
                      }
                    />
                  )}
                </AccordionSummary>

                <AccordionDetails>
                  <Container maxWidth="xs">
                    <Typography>{pedido.contacto.nombre_contacto}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      <Link
                        href={`https://api.whatsapp.com/send?phone=569${pedido.contacto.body_contacto.telefono}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconButton color="primary" size="small" className="mr-2">
                          <WhatsAppIcon />
                        </IconButton>
                      </Link>
                      {formatCelular(pedido.contacto.body_contacto.telefono)}
                    </Typography>

                    <Typography variant="body2" color="textSecondary" className="mt-2">
                      <Link href={`mailto:${pedido.contacto.body_contacto.email}`}>
                        <IconButton color="primary" size="small" className="mr-2">
                          <EmailIcon />
                        </IconButton>
                      </Link>{" "}
                      {pedido.contacto.body_contacto.email}
                    </Typography>
                  </Container>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expanded === "productos"} onChange={expandPanel("productos")}>
                <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
                  <ListItemIcon>
                    <ShoppingCartIcon />
                  </ListItemIcon>
                  <ListItemText primary={expanded === "productos" ? "Productos" : itemsCount} />
                </AccordionSummary>

                <AccordionDetails>
                  <Productos />
                </AccordionDetails>
              </Accordion>

              {delivery && delivery.content && (
                <Accordion expanded={expanded === "entrega"} onChange={expandPanel("entrega")}>
                  <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
                    <ListItemIcon>{delivery.icon}</ListItemIcon>
                    {expanded === "entrega" ? (
                      <ListItemText primary="Entrega" />
                    ) : (
                      <ListItemText
                        primary={delivery.title}
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            {delivery.subtitle}
                          </Typography>
                        }
                      />
                    )}
                  </AccordionSummary>

                  <AccordionDetails>
                    <div>
                      <Typography>{delivery.title}</Typography>
                      {delivery.content()}
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}

              {payment && payment.content && (
                <Accordion expanded={expanded === "pago"} onChange={expandPanel("pago")}>
                  <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
                    <ListItemIcon>{payment.icon}</ListItemIcon>
                    {expanded === "pago" ? (
                      <ListItemText primary="Pago" />
                    ) : (
                      <ListItemText primary={payment.title} secondary={payment.subtitle} />
                    )}
                  </AccordionSummary>

                  <AccordionDetails>
                    <div>
                      <Typography>{payment.title}</Typography>
                      {payment.content()}
                    </div>
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion expanded={expanded === "total"} onChange={expandPanel("total")}>
                <AccordionSummary className={classes.summary} expandIcon={<ExpandMoreIcon />}>
                  <ListItemIcon>
                    <MonetizationOnIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      expanded === "total" ? (
                        "Total"
                      ) : (
                        <NumberFormat
                          value={pedido.body_ticket.pago.total || 0}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="$"
                          displayType={"text"}
                        />
                      )
                    }
                  />
                </AccordionSummary>

                <AccordionDetails>
                  <Totales />
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Container>
        ) : (
          <Container className={classes.container}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Fecha />
              </Grid>

              <Grid item xs={12}>
                <Estado />
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {/* TODO}
                  <Grid item xs={12}>
                    <Nota />
                  </Grid>
                  {*/}

                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        className={classes.cardHeader}
                        avatar={<PersonIcon />}
                        title={pedido.contacto.nombre_contacto}
                      />
                      <CardContent className={classes.cardContent}>
                        <Typography variant="body2" color="textSecondary">
                          <Link
                            href={`https://api.whatsapp.com/send?phone=569${pedido.contacto.body_contacto.telefono}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton color="primary" size="small" className="mr-2">
                              <WhatsAppIcon />
                            </IconButton>
                          </Link>
                          {formatCelular(pedido.contacto.body_contacto.telefono)}
                        </Typography>

                        <Typography variant="body2" color="textSecondary" className="mt-2">
                          <Link href={`mailto:${pedido.contacto.body_contacto.email}`}>
                            <IconButton color="primary" size="small" className="mr-2">
                              <EmailIcon />
                            </IconButton>
                          </Link>{" "}
                          {pedido.contacto.body_contacto.email}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {delivery && delivery.content && (
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader
                          className={classes.cardHeader}
                          avatar={delivery.icon}
                          title={delivery.title}
                        />
                        <CardContent className={classes.cardContent}>
                          {delivery.content()}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {payment && payment.content && (
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader
                          className={classes.cardHeader}
                          avatar={payment.icon}
                          title={payment.title}
                          subheader={payment.subtitle}
                        />
                        <CardContent className={classes.cardContent}>
                          {payment.content()}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        className={classes.cardHeader}
                        avatar={<MonetizationOnIcon />}
                        title="Totales"
                      />
                      <CardContent className={classes.cardContent}>
                        <Totales />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader
                    className={classes.cardHeader}
                    avatar={<ShoppingCartIcon />}
                    title={itemsCount}
                  />
                  <CardContent className={classes.cardContent}>
                    <Productos />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        )}
      </div>
    </>
  );
};

export default PedidoDetails;
